import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
// import Select from "react-select";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import axios from "../api/axios";
import FindModuleById from '.././FindModuleById';
import { useFormik, FormikProvider, FieldArray } from "formik";
import { Apitoken, Authtoken } from "../Global";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DropzoneComponent from "./DropzoneComponent";
import * as Yup from 'yup';

// Validation Schema
const validationSchema = Yup.object({
  pallets: Yup.array().of(
    Yup.object({
      name: Yup.string().required('Product name is required'),
      product_type: Yup.string().required('Product type is required'),
      description: Yup.string().required('Description is required'),
    })
  ),
});

const EditProduct = () => {
  const { id } = useParams();
  const history = useHistory();

  // State management
  const [state, setState] = useState({
    palletlist: [],
    menu: false,
    permission: {},
    images: [],
    ordersLists: [],
    loading: true,
    error: null,
    submitting: false,
  });

  // Image cropper states
  const [cropperState, setCropperState] = useState({
    currentImageIndex: 0,
    crop: { x: 0, y: 0 },
    zoom: 1,
    rotation: 0,
    croppedAreaPixels: null,
    showCropper: false,
    activePalletIndex: null,
  });

  // Update state helper
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Update cropper state helper
  const updateCropperState = useCallback((updates) => {
    setCropperState(prev => ({ ...prev, ...updates }));
  }, []);

  // Memoized permission check
  const hasUpdatePermission = useMemo(() => {
    return state.permission && state.permission.update === 1;
  }, [state.permission]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        updateState({ loading: true, error: null });
        
        // Set permission
        const permissionData = FindModuleById(447);
        updateState({ permission: permissionData });

        // Fetch product data and product types in parallel
        const [productResponse, productTypesResponse] = await Promise.all([
          axios.get(`/products/edit/${id}`, {
            headers: { Authorization: `Bearer ${Authtoken}`, Apitoken: Apitoken },
          }),
          axios.get("/helpers/products-type", {
            headers: { Authorization: `Bearer ${Authtoken}`, Apitoken: Apitoken },
          })
        ]);

        // Handle product data
        if (productResponse.data.code === 200) {
          const fetchedPallets = productResponse.data.data.product;
          const allImages = fetchedPallets.map((pallet) =>
            pallet.pictures.map((picture) => picture)
          );
          
          updateState({ 
            palletlist: fetchedPallets,
            images: allImages,
          });
        } else {
          throw new Error('Failed to fetch product data');
        }

        // Handle product types
        if (productTypesResponse.data.code === 200) {
          const supplierTypes = productTypesResponse.data.data.product_types;
          const supplierTypesList = supplierTypes.map((cat, index) => ({
            label: cat.title,
            value: cat.id,
            key: `product-${index}`,
          }));
          updateState({ ordersLists: supplierTypesList });
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        updateState({ error: error.message });
        toast.error('Failed to load product data');
      } finally {
        updateState({ loading: false });
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, updateState]);

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      id: id,
      pallets: state.palletlist,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        updateState({ submitting: true });
        
        // Only send the last pallet (as per original logic)
        const pallets12 = values.pallets.slice(-1);
        const submissionData = { ...values, pallets: pallets12 };

        const response = await axios.post("/products/update-2", submissionData, {
          headers: { Authorization: `Bearer ${Authtoken}`, Apitoken: Apitoken },
        });

        if (response.data.code === 200) {
          toast.success(response.data.message);
          setTimeout(() => {
            history.push(`/products`);
          }, 500);
        } else {
          throw new Error(response.data.message || 'Update failed');
        }
      } catch (error) {
        console.error('Error updating product:', error);
        toast.error(error.response?.data?.message || error.message || 'Update failed');
      } finally {
        setSubmitting(false);
        updateState({ submitting: false });
      }
    },
  });

  // Image handling utilities
  const fileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }, []);

  const resizeImage = useCallback((base64Str, maxWidth, maxHeight) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = (maxHeight / width) * height;
            width = maxWidth;
          } else {
            width = (maxWidth / height) * width;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.9));
      };
    });
  }, []);

  const getCroppedImage = useCallback(async (imageSrc) => {
    const image = new Image();
    image.src = imageSrc;

    return new Promise((resolve, reject) => {
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const croppedWidth = cropperState.croppedAreaPixels.width * scaleX;
        const croppedHeight = cropperState.croppedAreaPixels.height * scaleY;

        canvas.width = croppedWidth;
        canvas.height = croppedHeight;

        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((cropperState.rotation * Math.PI) / 180);
        ctx.translate(-canvas.width / 2, -canvas.height / 2);

        ctx.drawImage(
          image,
          cropperState.croppedAreaPixels.x * scaleX,
          cropperState.croppedAreaPixels.y * scaleY,
          croppedWidth,
          croppedHeight,
          0,
          0,
          canvas.width,
          canvas.height
        );

        const base64Image = canvas.toDataURL("image/jpeg", 0.9);
        resolve(base64Image);
      };
      image.onerror = (err) => reject(err);
    });
  }, [cropperState.croppedAreaPixels, cropperState.rotation]);

  // Image handlers
  const onDrop = useCallback(async (acceptedFiles, palletIndex) => {
    const filesArray = Array.from(acceptedFiles);
    const currentPictures = formik.values.pallets[palletIndex]?.pictures || [];

    try {
      const resizedImages = await Promise.all(
        filesArray.map(async (file) => {
          const base64Image = await fileToBase64(file);
          return resizeImage(base64Image, 1000, 1000);
        })
      );

      let updatedPictures = [...currentPictures, ...resizedImages];
      if (updatedPictures.length > 6) {
        updatedPictures = updatedPictures.slice(0, 6);
        toast.warning('Maximum 6 images allowed per product');
      }

      formik.setFieldValue(`pallets[${palletIndex}].pictures`, updatedPictures);

      setState(prevState => {
        const newImages = [...prevState.images];
        newImages[palletIndex] = updatedPictures;
        return { ...prevState, images: newImages };
      });
    } catch (error) {
      console.error("Error processing images:", error);
      toast.error('Failed to process images');
    }
  }, [formik, fileToBase64, resizeImage]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    updateCropperState({ croppedAreaPixels });
  }, [updateCropperState]);

  const handleSaveCroppedImage = useCallback(async (palletIndex, imgIndex) => {
    try {
      const croppedImageUrl = await getCroppedImage(state.images[palletIndex][imgIndex]);
      const updatedImages = [...formik.values.pallets[palletIndex].pictures];
      updatedImages[imgIndex] = croppedImageUrl;
      
      formik.setFieldValue(`pallets[${palletIndex}].pictures`, updatedImages);

      setState(prevState => {
        const newImages = [...prevState.images];
        newImages[palletIndex] = [...newImages[palletIndex]];
        newImages[palletIndex][imgIndex] = croppedImageUrl;
        return { ...prevState, images: newImages };
      });

      updateCropperState({ showCropper: false });
      toast.success('Image cropped successfully');
    } catch (error) {
      console.error("Error saving cropped image:", error);
      toast.error('Failed to crop image');
    }
  }, [state.images, formik, getCroppedImage, updateCropperState]);

  const deletepalletImagefromDB = useCallback(async (itemId) => {
    try {
      const response = await axios.get(`/quality-report/pallet-image/delete/${itemId}`, {
        headers: { Authorization: `Bearer ${Authtoken}`, Apitoken: Apitoken },
      });
      return { status: response.data.code === 200 ? 'ok' : 'false' };
    } catch (error) {
      console.error("Error deleting pallet image:", error);
      return { status: 'false' };
    }
  }, []);

  const handleDeleteImage = useCallback(async (palletIndex, imgIndex) => {
    const updatedPallets = [...formik.values.pallets];
    const currentPictures = [...(updatedPallets[palletIndex]?.pictures || [])];
    const imageToDelete = currentPictures[imgIndex];

    // Delete from database if it's a URL
    if (imageToDelete && imageToDelete.startsWith('http')) {
      try {
        const parsedUrl = new URL(imageToDelete);
        const imagePath = parsedUrl.pathname.substring(1).replace(/\//g, '--');
        const response = await deletepalletImagefromDB(imagePath);
        
        if (response.status !== 'ok') {
          toast.warning('Failed to delete image from server, but removed locally');
        }
      } catch (error) {
        console.error('Error parsing image URL:', error);
      }
    }

    // Remove from local state
    if (currentPictures.length > imgIndex) {
      currentPictures.splice(imgIndex, 1);
    }

    updatedPallets[palletIndex].pictures = currentPictures;
    formik.setFieldValue('pallets', updatedPallets);

    setState(prevState => {
      const newImages = [...prevState.images];
      newImages[palletIndex] = currentPictures;
      return { ...prevState, images: newImages };
    });

    // Update current image index if necessary
    if (imgIndex === cropperState.currentImageIndex) {
      updateCropperState({ currentImageIndex: Math.max(0, imgIndex - 1) });
    }

    toast.success('Image deleted successfully');
  }, [formik, cropperState.currentImageIndex, deletepalletImagefromDB, updateCropperState]);

  const handleImageClick = useCallback((palletIndex, imgIndex) => {
    updateCropperState({
      currentImageIndex: imgIndex,
      activePalletIndex: palletIndex,
      showCropper: true,
      crop: { x: 0, y: 0 },
      zoom: 1,
      rotation: 0,
    });
  }, [updateCropperState]);

  const handleMoveImage = useCallback((palletIndex, imgIndex, direction) => {
    const swapIndex = imgIndex + direction;
    if (swapIndex < 0 || swapIndex >= state.images[palletIndex]?.length) return;

    const updatedImages = [...state.images[palletIndex]];
    [updatedImages[imgIndex], updatedImages[swapIndex]] = [
      updatedImages[swapIndex],
      updatedImages[imgIndex],
    ];

    setState(prevState => {
      const newImages = [...prevState.images];
      newImages[palletIndex] = updatedImages;
      return { ...prevState, images: newImages };
    });

    formik.setFieldValue(`pallets[${palletIndex}].pictures`, updatedImages);
  }, [state.images, formik]);

  // const handleFileUpload = useCallback((event, palletIndex) => {
  //   const file = event.target.files[0];
  //   if (!file) return;

  //   const reader = new FileReader();
  //   reader.onloadend = () => {
  //     formik.setFieldValue(`pallets[${palletIndex}].file`, reader.result);
  //   };
  //   reader.readAsDataURL(file);
  // }, [formik]);
//   const handleFileUpload = useCallback((event, palletIndex) => {
//   const file = event.target.files[0];
//   if (!file) return;

//   // Directly set the file object, NOT base64
//   formik.setFieldValue(`pallets[${palletIndex}].file`, file);
// }, [formik]);


  // Loading state
  if (state.loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading product data...</Typography>
      </Box>
    );
  }

  // Error state
  if (state.error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" minHeight="50vh" justifyContent="center">
        <Typography variant="h6" color="error" gutterBottom>
          Error loading product data
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {state.error}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <div className={`main-wrapper ${state.menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => updateState({ menu: !state.menu })} />
      <Sidebar />
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3 className="page-title">Edit Product</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/profile">Profile</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/products">Product</Link>
                  </li>
                  <li className="breadcrumb-item active">Edit Product</li>
                </ul>
              </div>
            </div>
          </div>

          {!hasUpdatePermission && (
            <div className="alert alert-warning">
              <strong>Warning!</strong> You do not have permission to update products.
            </div>
          )}

          {hasUpdatePermission && (
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <FieldArray
                  name="pallets"
                  render={() => (
                    <div>
                      {formik.values.pallets.map((item, palletIndex) => (
                        <div key={palletIndex} className="card mb-4">
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-6">
                                <div className="form-group">
                                  <label>Product Name *</label>
                                  <input
                                    type="text"
                                    className={`form-control ${
                                      formik.touched.pallets?.[palletIndex]?.name && 
                                      formik.errors.pallets?.[palletIndex]?.name ? 'is-invalid' : ''
                                    }`}
                                    name={`pallets[${palletIndex}].name`}
                                    value={formik.values.pallets[palletIndex]?.name || ''}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter product name"
                                  />
                                  {formik.touched.pallets?.[palletIndex]?.name && 
                                   formik.errors.pallets?.[palletIndex]?.name && (
                                    <div className="invalid-feedback">
                                      {formik.errors.pallets[palletIndex].name}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div className="form-group">
                                  <label>Product Type *</label>
                                  {/* {state.ordersLists.length > 0 && (
                                    <Select
                                      name={`pallets[${palletIndex}].product_type`}
                                      options={state.ordersLists}
                                      value={state.ordersLists.find(option => 
                                        option.value == formik.values.pallets[palletIndex]?.product_type
                                      ) || null}
                                      onChange={(selectedOption) => {
                                        formik.setFieldValue(
                                          `pallets[${palletIndex}].product_type`,
                                          selectedOption?.value || ''
                                        );
                                      }}
                                      onBlur={formik.handleBlur}
                                      placeholder="Select product type"
                                      className="w-100"
                                      isClearable
                                    />
                                  )}
                                  {formik.touched.pallets?.[palletIndex]?.product_type && 
                                   formik.errors.pallets?.[palletIndex]?.product_type && (
                                    <div className="text-danger small mt-1">
                                      {formik.errors.pallets[palletIndex].product_type}
                                    </div>
                                  )} */}
                                  <input
                                    type="text"
                                    className={`form-control ${
                                      formik.touched.pallets?.[palletIndex]?.product_type && 
                                      formik.errors.pallets?.[palletIndex]?.product_type ? 'is-invalid' : ''
                                    }`}
                                    name={`pallets[${palletIndex}].product_type`}
                                    value={formik.values.pallets[palletIndex]?.product_type || ''}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter Product Type"
                                  />
                                  {formik.touched.pallets?.[palletIndex]?.product_type && 
                                   formik.errors.pallets?.[palletIndex]?.product_type && (
                                    <div className="invalid-feedback">
                                      {formik.errors.pallets[palletIndex].product_type}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div className="form-group">
                                  <label>Attach </label>
                                  <input
                                    className="form-control"
                                    type="file"
                                    name={`pallets[${palletIndex}].file`}
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    //onChange={(event) => handleFileUpload(event, palletIndex)}
                                    onChange={(event) => {
                                                //getFileData(event);

                                          const file_link = event.target.files[0];
                                          //console.log("file:", file_link);
                                          //const fileName = file_link.name;
                                        // const fileName = file_link.name;
                                          const reader = new FileReader();

                                          reader.onloadend = () => {
                                            formik.setFieldValue(
                                              `pallets[${palletIndex}].file`,
                                              reader.result
                                            );
                                          };
        
                                          if (file_link) {
                                            reader.readAsDataURL(file_link);
                                          }
                                        }}
                                  />
                                  
                                </div>
                              </div>

                              <div className="col-md-12">
                                <div className="form-group">
                                  <label>Description *</label>
                                  <textarea
                                    className={`form-control ${
                                      formik.touched.pallets?.[palletIndex]?.description && 
                                      formik.errors.pallets?.[palletIndex]?.description ? 'is-invalid' : ''
                                    }`}
                                    name={`pallets[${palletIndex}].description`}
                                    value={formik.values.pallets[palletIndex]?.description || ''}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    rows="4"
                                    placeholder="Enter product description"
                                  />
                                  {formik.touched.pallets?.[palletIndex]?.description && 
                                   formik.errors.pallets?.[palletIndex]?.description && (
                                    <div className="invalid-feedback">
                                      {formik.errors.pallets[palletIndex].description}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="col-md-12">
                                <div className="form-group">
                                  <label>Product Images (Max 6)</label>
                                  <DropzoneComponent
                                    palletIndex={palletIndex}
                                    onDrop={onDrop}
                                  />
                                  
                                  <div className="image-preview-container mt-3">
                                    <div className="row">
                                      {state.images[palletIndex]?.map((image, imgIndex) => (
                                        <div key={imgIndex} className="col-md-2 mb-3">
                                          <div className="image-preview-card">
                                            <img
                                              src={image}
                                              alt={`preview ${imgIndex}`}
                                              className="img-thumbnail"
                                              style={{
                                                width: "100%",
                                                height: "120px",
                                                objectFit: "cover",
                                                cursor: "pointer",
                                                border: cropperState.currentImageIndex === imgIndex ? 
                                                  "2px solid #007bff" : "1px solid #dee2e6",
                                              }}
                                              onClick={() => handleImageClick(palletIndex, imgIndex)}
                                            />
                                            <div className="image-controls mt-2">
                                              <div className="btn-group btn-group-sm w-100">
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  disabled={imgIndex === 0}
                                                  onClick={() => handleMoveImage(palletIndex, imgIndex, -1)}
                                                >
                                                  ↑
                                                </Button>
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  disabled={imgIndex === state.images[palletIndex].length - 1}
                                                  onClick={() => handleMoveImage(palletIndex, imgIndex, 1)}
                                                >
                                                  ↓
                                                </Button>
                                              </div>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                color="error"
                                                fullWidth
                                                onClick={() => handleDeleteImage(palletIndex, imgIndex)}
                                                className="mt-1"
                                              >
                                                Delete
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                />

                <div className="text-end mt-4">
                  {/* <Button
                    type="submit"
                    variant="contained"
                    className="btn btn-primary"
                    size="large"
                    disabled={formik.isSubmitting || state.submitting}
                    // startIcon={state.submitting && <CircularProgress size={20} />}
                  >
                    {state.submitting ? 'Updating...' : 'Update Product'}
                  </Button> */}
                   <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={formik.isSubmitting}
                      
                    >
                      Update Product
                    </button>
                </div>
              </form>
            </FormikProvider>
          )}

          {/* Image Cropper Dialog */}
          {state.images[cropperState.activePalletIndex]?.length > 0 && (
            <Dialog
              open={cropperState.showCropper && cropperState.activePalletIndex !== null}
              onClose={() => updateCropperState({ showCropper: false })}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle>Image Editor</DialogTitle>
              <DialogContent>
                <div style={{ position: "relative", height: "400px", width: "100%" }}>
                  <Cropper
                    image={state.images[cropperState.activePalletIndex]?.[cropperState.currentImageIndex]}
                    crop={cropperState.crop}
                    zoom={cropperState.zoom}
                    aspect={1}
                    rotation={cropperState.rotation}
                    onCropChange={(crop) => updateCropperState({ crop })}
                    onZoomChange={(zoom) => updateCropperState({ zoom })}
                    onCropComplete={onCropComplete}
                  />
                </div>

                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>Rotation</Typography>
                  <Slider
                    value={cropperState.rotation}
                    onChange={(e, newValue) => updateCropperState({ rotation: newValue })}
                    min={0}
                    max={360}
                    step={1}
                    marks={[
                      { value: 0, label: '0°' },
                      { value: 90, label: '90°' },
                      { value: 180, label: '180°' },
                      { value: 270, label: '270°' },
                    ]}
                  />
                </Box>

                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>Zoom</Typography>
                  <Slider
                    value={cropperState.zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    onChange={(e, newZoom) => updateCropperState({ zoom: newZoom })}
                    marks={[
                      { value: 1, label: '1x' },
                      { value: 2, label: '2x' },
                      { value: 3, label: '3x' },
                    ]}
                  />
                </Box>
              </DialogContent>

              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={() => updateCropperState({ showCropper: false })}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSaveCroppedImage(
                    cropperState.activePalletIndex, 
                    cropperState.currentImageIndex
                  )}
                >
                  Save Cropped Image
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditProduct;